import { type ButtonHTMLAttributes, type MouseEvent } from 'react';
import ButtonContent from './ButtonContent';
import { buttonClassName, type ButtonLook } from './buttonStyles';
import { type IconName } from './Icon';

// Every action in the admin, ported from design/files_v1.2/droog-admin/
// bundle.js (Button). Added to the design (BHP-1213): `loading` — ignores
// clicks so it cannot run twice, `aria-busy`, a spinner. It stays focusable
// (aria-disabled, not disabled): a native disabled button drops focus to the
// body, and a keyboard user would start over. For navigation use ButtonLink.

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonLook {
  icon?: IconName;
  iconRight?: IconName;
  loading?: boolean;
}

export default function Button({
  variant,
  size,
  block,
  icon,
  iconRight,
  loading = false,
  disabled,
  type = 'button',
  className,
  children,
  onClick,
  'aria-disabled': ariaDisabled,
  ...rest
}: ButtonProps): JSX.Element {
  // aria-disabled from the caller (e.g. Annuleren while a dialog saves)
  // behaves like loading without the spinner: focusable, clicks ignored.
  const unavailable =
    loading || ariaDisabled === true || ariaDisabled === 'true';
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (unavailable) {
      // Also stops a submit button from submitting its form.
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <button
      {...rest}
      type={type}
      disabled={disabled}
      aria-disabled={unavailable ? true : undefined}
      aria-busy={loading ? true : undefined}
      onClick={handleClick}
      className={buttonClassName({
        variant,
        size,
        block,
        inert: disabled === true || unavailable,
        className
      })}
    >
      <ButtonContent
        size={size}
        icon={icon}
        iconRight={iconRight}
        loading={loading}
      >
        {children}
      </ButtonContent>
    </button>
  );
}
